import { makeAutoObservable } from 'mobx';
import _ from 'lodash';
import { toast } from 'react-toastify';
import {
    IChartConfigItem,
    IEngagementEmotion,
    IEngagementLevel,
} from '../types/Config';
import {
    EventColor,
    LevelData,
} from '../components/ValueTimelineGraph/interfaces';
import { AudioService } from '../services';
import localStorageWorker from '../utils/localStorageWorker';

const DEFAULT_LEVELS: IEngagementLevel[] = [
    {
        id: '0',
        name: 'Very Low',
        color: '#c52700',
        value: 11,
    },
    {
        id: '1',
        name: 'Low',
        color: '#e2c000',
        value: 13,
    },
    {
        id: '2',
        name: 'Medium',
        color: '#859c00',
        value: 16,
    },
    {
        id: '3',
        name: 'High',
        color: '#0c8d00',
        value: 18,
    },
    {
        id: '4',
        name: 'Very High',
        color: '#086300',
        value: 100,
    },
];

const DEFAULT_CHARTS_CONFIG: IChartConfigItem[] = [
    {
        name: 'Conversational Impact',
        visible: true,
    },
    {
        name: 'Self-Rating',
        visible: false,
    },
    {
        name: 'Self-Awareness',
        visible: true,
    },
    {
        name: 'Turn Taking',
        visible: true,
    },
    {
        name: 'Perceived Engagement',
        visible: true,
    },
    {
        name: 'Audio Statistics',
        visible: true,
    },
    {
        name: 'Engagement summary',
        visible: true,
    },
];

const ENGAGEMENT_CONFIG_LS_STORAGE = 'engagement-config';
const CHARTS_CONFIG_LS_STORAGE = 'charts-config';
const EMOTIONS_CONFIG_LS_STORAGE = 'emotions-config';
const SECONDS_TO_PLAY_HIGHLIGHTS_LS_STORAGE = 'higlights-seconds';
const AVARAGE_SECONDS_LS_STORAGE = 'avarage-seconds';
const TRIM_SIM_START_LS_STORAGE = 'trim-sim-start';

export default class ConfigStore {
    engagementChartLevels: IEngagementLevel[] = [];

    emotionItems: IEngagementEmotion[] | null =
        localStorageWorker.getFromLS(EMOTIONS_CONFIG_LS_STORAGE) || null;

    // To save items that fetched from back-end
    defaultEmotionItems: IEngagementEmotion[] = [];

    avrSeconds: number | null =
        localStorageWorker.getFromLS(AVARAGE_SECONDS_LS_STORAGE) || null;

    secondsToPlayHighlight: number =
        localStorageWorker.getFromLS(SECONDS_TO_PLAY_HIGHLIGHTS_LS_STORAGE) ||
        10;

    chartsConfig: IChartConfigItem[] = [];

    trimBeforeSimStartEvents: boolean =
        localStorageWorker.getFromLS(TRIM_SIM_START_LS_STORAGE) || true;

    error: any;

    private audioService: typeof AudioService;

    constructor() {
        this.audioService = AudioService;

        const sortedDefaultChartsConfig = _.orderBy(
            DEFAULT_CHARTS_CONFIG,
            ['name'],
            ['asc'],
        );
        const chartsConfigFromLocalStorage: IChartConfigItem[] | undefined =
            localStorageWorker.getFromLS(CHARTS_CONFIG_LS_STORAGE);
        if (chartsConfigFromLocalStorage) {
            const chartsConfigItemNamesFromLocalStorage: string[] =
                chartsConfigFromLocalStorage.map((item) => item.name);
            const defaultChartsConfigItemNames: string[] =
                sortedDefaultChartsConfig.map((item) => item.name);
            const isNamesAndLengthEqual: boolean = _.isEqual(
                chartsConfigItemNamesFromLocalStorage,
                defaultChartsConfigItemNames,
            );
            this.chartsConfig = isNamesAndLengthEqual
                ? chartsConfigFromLocalStorage
                : sortedDefaultChartsConfig;
        } else {
            this.chartsConfig = sortedDefaultChartsConfig;
        }

        const engagementChartLevelsFromLocalStorage:
            | IEngagementLevel[]
            | undefined = localStorageWorker.getFromLS(
            ENGAGEMENT_CONFIG_LS_STORAGE,
        );
        this.engagementChartLevels =
            engagementChartLevelsFromLocalStorage &&
            engagementChartLevelsFromLocalStorage.length ===
                DEFAULT_LEVELS.length
                ? engagementChartLevelsFromLocalStorage
                : DEFAULT_LEVELS;

        makeAutoObservable(this);
    }

    get isChartLevelsDefault() {
        return _.isEqual(this.engagementChartLevels, DEFAULT_LEVELS);
    }

    get isEmotionItemsDefault() {
        return _.isEqual(this.emotionItems, this.defaultEmotionItems);
    }

    get isEngagementChartLevelsDefault() {
        return _.isEqual(this.engagementChartLevels, DEFAULT_LEVELS);
    }

    get engagementChartConfig() {
        const eventColors: EventColor[] = [];
        const possibleYValues: number[] = [];
        const levelData: LevelData[] = [];
        this.engagementChartLevels.forEach((level, index) => {
            const { id, name, color } = level;
            const newLevelData: LevelData = {
                title: name,
                level: index,
                id,
            };
            const eventColor: EventColor = {
                eventIdPattern: new RegExp(`^\\b${id}{1}\\b`),
                itemId: name,
                color,
            };
            eventColors.push(eventColor);
            possibleYValues.push(index);
            levelData.push(newLevelData);
        });
        return {
            eventColors,
            possibleYValues,
            levelData,
        };
    }

    setEngagementChartLevels(engagementChartLevels: IEngagementLevel[]): void {
        const newEngagementChartLeves = engagementChartLevels.sort(
            (current, prev) => (current.value > prev.value ? 1 : -1),
        );
        this.engagementChartLevels = newEngagementChartLeves;
        localStorageWorker.setToLS(
            ENGAGEMENT_CONFIG_LS_STORAGE,
            newEngagementChartLeves,
        );
    }

    setChartsConfig(chartsConfig: IChartConfigItem[]): void {
        const sortedNewChartsConfig = _.orderBy(
            chartsConfig,
            ['name'],
            ['asc'],
        );
        this.chartsConfig = sortedNewChartsConfig;
        localStorageWorker.setToLS(
            CHARTS_CONFIG_LS_STORAGE,
            sortedNewChartsConfig,
        );
    }

    setEmotionItems(emotionItems: IEngagementEmotion[] | null): void {
        if (emotionItems) {
            const sortedNewEmotionsItems = _.orderBy(
                emotionItems,
                ['id'],
                ['asc'],
            );
            this.emotionItems = sortedNewEmotionsItems;
            localStorageWorker.setToLS(
                EMOTIONS_CONFIG_LS_STORAGE,
                sortedNewEmotionsItems,
            );
        } else {
            this.emotionItems = null;
            localStorageWorker.deleteFromLS(EMOTIONS_CONFIG_LS_STORAGE);
        }
    }

    // To save items that fetched from back-end
    private setDefaultEmotionItems(emotionItems: IEngagementEmotion[]): void {
        this.defaultEmotionItems = _.orderBy(emotionItems, ['id'], ['asc']);
    }

    setSecondsToPlayHighlight(secondsToPlayHighlight: number): void {
        this.secondsToPlayHighlight = secondsToPlayHighlight;
        localStorageWorker.setToLS(
            SECONDS_TO_PLAY_HIGHLIGHTS_LS_STORAGE,
            secondsToPlayHighlight,
        );
    }

    setAvrSeconds(avrSeconds: number | null): void {
        this.avrSeconds = avrSeconds;
        localStorageWorker.setToLS(AVARAGE_SECONDS_LS_STORAGE, avrSeconds);
    }

    setTrimBeforeSimStartEvents(value: boolean): void {
        this.trimBeforeSimStartEvents = value;
        localStorageWorker.setToLS(TRIM_SIM_START_LS_STORAGE, value);
    }

    resetEngagementChartLevelsToDefault(): void {
        this.setEngagementChartLevels(DEFAULT_LEVELS);
    }

    resetEngagementEmotionsToDefault(): void {
        this.setEmotionItems(this.defaultEmotionItems);
    }

    private setError(error: any): void {
        // eslint-disable-next-line no-console
        console.error(error);
        this.error = error;
        toast.error(error.message);
    }

    async fetchDefaultEmotions(): Promise<void> {
        this.setDefaultEmotionItems([]);
        try {
            const data: { [key: string]: number } =
                await this.audioService.fetchFmmEmotionsDefaultConfig();
            const keys: string[] = Object.keys(data);
            const emotionItems: IEngagementEmotion[] = keys.map(
                (key: string) => ({
                    id: key,
                    value: data[key],
                }),
            );
            this.setDefaultEmotionItems(emotionItems);
        } catch (error) {
            this.setError(error);
        }
    }
}
